import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationApi,
  Notification,
  NotificationEventType,
  NotificationStatus,
  NotificationEventTypeLabels,
  NotificationStatusLabels,
  NotificationListParams,
} from '../services/notificationApi';

const NotificationHistory: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState<{
    eventType: NotificationEventType | '';
    status: NotificationStatus | '';
    startDate: string;
    endDate: string;
  }>({
    eventType: '',
    status: '',
    startDate: '',
    endDate: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [detailModal, setDetailModal] = useState<{ open: boolean; notification: Notification | null }>({
    open: false,
    notification: null,
  });

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params: NotificationListParams = { page, limit };
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await notificationApi.getNotifications(params);
      setNotifications(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      setMessage({ type: 'error', text: '알림 내역을 불러오는데 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchNotifications();
  };

  const handleRetry = async (id: number) => {
    try {
      await notificationApi.retryNotification(id);
      setMessage({ type: 'success', text: '알림이 재발송되었습니다.' });
      fetchNotifications();
    } catch (error) {
      setMessage({ type: 'error', text: '알림 재발송에 실패했습니다.' });
    }
  };

  const getStatusBadge = (status: NotificationStatus) => {
    const styles = {
      [NotificationStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [NotificationStatus.SENT]: 'bg-green-100 text-green-800',
      [NotificationStatus.FAILED]: 'bg-red-100 text-red-800',
    };
    const icons = {
      [NotificationStatus.PENDING]: '⏳',
      [NotificationStatus.SENT]: '✅',
      [NotificationStatus.FAILED]: '❌',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {icons[status]} {NotificationStatusLabels[status]}
      </span>
    );
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl">📋</span>
          <h1 className="text-xl font-bold text-gray-800">알림 발송 내역</h1>
        </div>
        <button
          onClick={() => navigate('/notification-settings')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          알림 설정
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이벤트 유형</label>
            <select
              value={filters.eventType}
              onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value as NotificationEventType | '' }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">전체</option>
              {Object.entries(NotificationEventTypeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as NotificationStatus | '' }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">전체</option>
              {Object.entries(NotificationStatusLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">시작일</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">종료일</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            검색
          </button>
          <button
            onClick={fetchNotifications}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="새로고침"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">유형</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">제목</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">수신자</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">생성일시</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">발송일시</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">액션</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    알림 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
                        {NotificationEventTypeLabels[notification.event_type] || notification.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-sm text-gray-900">
                      {notification.event_title}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {notification.recipient_email}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(notification.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(notification.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(notification.sent_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => setDetailModal({ open: true, notification })}
                          className="p-1 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="상세보기"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {notification.status === NotificationStatus.FAILED && (
                          <button
                            onClick={() => handleRetry(notification.id)}
                            className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                            title="재발송"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {total}건 중 {(page - 1) * limit + 1}-{Math.min(page * limit, total)}건
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal.open && detailModal.notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">알림 상세</h3>
              <button
                onClick={() => setDetailModal({ open: false, notification: null })}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">유형:</span>
                <span>{NotificationEventTypeLabels[detailModal.notification.event_type]}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">제목:</span>
                <span>{detailModal.notification.event_title}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">수신자:</span>
                <span>{detailModal.notification.recipient_email}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">상태:</span>
                <span>{getStatusBadge(detailModal.notification.status)}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">생성일시:</span>
                <span>{formatDate(detailModal.notification.createdAt)}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="font-medium text-gray-600">발송일시:</span>
                <span>{formatDate(detailModal.notification.sent_at)}</span>
              </div>
              {detailModal.notification.error_message && (
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="font-medium text-red-600">오류:</span>
                  <span className="text-red-600">{detailModal.notification.error_message}</span>
                </div>
              )}
            </div>

            <div className="mt-4">
              <span className="font-medium text-gray-600 block mb-2">상세 데이터:</span>
              <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-sm overflow-x-auto">
                {JSON.stringify(detailModal.notification.event_detail, null, 2)}
              </pre>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              {detailModal.notification.status === NotificationStatus.FAILED && (
                <button
                  onClick={() => {
                    handleRetry(detailModal.notification!.id);
                    setDetailModal({ open: false, notification: null });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  재발송
                </button>
              )}
              <button
                onClick={() => setDetailModal({ open: false, notification: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;
